<template>
  <div class="user-password-container password-page nav-content-inset">
    <el-card class="user-password-card">
      <template #header>
        <h2 class="card-title">账号安全</h2>
      </template>

      <p class="page-hint">
        <strong>登录名</strong>用于密码登录（不是展示用的昵称）；
        <strong>昵称</strong>在「个人朋友信息」里修改，仅影响展示。
        微信扫码账号建议尽快设置登录名与密码，并绑定真实邮箱或手机以便找回与验证码登录。
      </p>

      <el-alert
        v-if="setupRedirect && (usernameAuto || passwordUnset)"
        class="setup-alert"
        type="warning"
        :closable="false"
        show-icon
        title="请先完善登录信息"
        description="微信扫码已为您创建账号。请设置易记的登录名与登录密码；完成后可使用「登录名 + 密码」登录，并自动返回原页面。"
      />

      <!-- 合并完善：首次微信用户 -->
      <section v-if="needsCombinedSetup" class="account-section account-section--primary">
        <h3 class="section-title">完善登录信息</h3>
        <p v-if="!(usernameAuto && currentUsername)" class="section-hint">请设置登录名与密码，之后即可用「登录名 + 密码」登录。</p>
        <el-form
          label-position="top"
          class="user-settings-form"
          @submit.prevent="submitCombinedSetup"
        >
          <el-form-item v-if="usernameChangeAllowed" required>
            <template #label>登录名</template>
            <p v-if="usernameAuto && currentUsername" class="field-rule">
              当前临时登录名 <span class="mono">{{ currentUsername }}</span>
              （系统分配，不是昵称；请在下方改为自己的登录名）
            </p>
            <el-input
              v-model="setup.username"
              maxlength="32"
              clearable
              placeholder="设置你的登录名"
              autocomplete="username"
            />
            <p class="field-rule">{{ wechatUsernameHint }}</p>
          </el-form-item>
          <el-form-item v-if="passwordUnset" label="密码" required>
            <el-input
              v-model="setup.newPassword"
              type="password"
              show-password
              maxlength="64"
              placeholder="请设置密码"
              autocomplete="new-password"
            />
            <p class="field-rule">{{ passwordHint }}</p>
          </el-form-item>
          <el-form-item v-if="passwordUnset" label="确认密码" required>
            <el-input
              v-model="setup.confirmPassword"
              type="password"
              show-password
              maxlength="64"
              placeholder="请再次输入密码"
              autocomplete="new-password"
            />
          </el-form-item>
          <el-button type="primary" :loading="setupSaving" native-type="submit">
            保存登录名与密码
          </el-button>
        </el-form>
      </section>

      <!-- 登录名（合并完善流程中已在上方展示，此处不重复） -->
      <section v-if="!needsCombinedSetup" class="account-section">
        <h3 class="section-title">登录名</h3>
        <div v-if="usernameAuto" class="inline-tags">
          <el-tag type="warning" size="small" effect="light">待设置</el-tag>
          <span class="mono">{{ currentUsername || '—' }}</span>
        </div>
        <div v-else class="username-display">
          当前登录名：<span class="mono">{{ currentUsername || '—' }}</span>
        </div>

        <template v-if="usernameChangeAllowed">
          <el-form label-position="top" class="user-settings-form" @submit.prevent="submitUsernameOnly">
            <el-form-item>
              <template #label>设置登录名</template>
              <p v-if="usernameAuto && currentUsername" class="field-rule">
                当前临时登录名 <span class="mono">{{ currentUsername }}</span>
                （系统分配，不是昵称；请尽快改为自己的登录名）
              </p>
              <el-input
                v-model="newUsername"
                maxlength="32"
                clearable
                placeholder="设置你的登录名"
              />
              <p class="field-rule">{{ wechatUsernameHint }}</p>
            </el-form-item>
            <el-button type="primary" :loading="usernameSaving" native-type="submit">
              保存登录名
            </el-button>
          </el-form>
        </template>
        <p v-else class="field-hint">
          登录名已设置，不可再修改。仅系统自动生成的临时登录名（如 wx_xxxxxxxxxx）允许修改一次。
        </p>
      </section>

      <!-- 密码 -->
      <section v-if="!needsCombinedSetup || !passwordUnset" class="account-section">
        <h3 class="section-title">{{ passwordUnset ? '设置密码' : '修改密码' }}</h3>
        <p v-if="passwordUnset" class="section-hint">
          尚未设置登录密码。设置后可用登录名 + 密码登录。
        </p>
        <el-form
          ref="pwdFormRef"
          :model="pwdForm"
          :rules="pwdRules"
          label-position="top"
          class="user-settings-form"
          @submit.prevent="submitPassword"
        >
          <el-form-item :label="passwordUnset ? '密码' : '新密码'" prop="newPassword">
            <el-input
              v-model="pwdForm.newPassword"
              type="password"
              show-password
              maxlength="64"
              autocomplete="new-password"
              :placeholder="passwordUnset ? '请设置密码' : '请输入新密码'"
            />
            <p class="field-rule">{{ passwordHint }}</p>
          </el-form-item>
          <el-form-item label="确认密码" prop="confirmPassword">
            <el-input
              v-model="pwdForm.confirmPassword"
              type="password"
              show-password
              maxlength="64"
              autocomplete="new-password"
              placeholder="请再次输入密码"
            />
          </el-form-item>
          <el-button type="primary" :loading="pwdSaving" native-type="submit">保存密码</el-button>
        </el-form>
      </section>

      <!-- 邮箱 -->
      <section class="account-section">
        <h3 class="section-title">绑定邮箱</h3>
        <p class="section-hint">
          绑定并验证真实邮箱后，可用邮箱找回密码。微信占位邮箱无法用于找回。
        </p>
        <div v-if="emailVerified && email" class="email-bound">
          已绑定：<span class="mono">{{ email }}</span>
          <el-tag type="success" size="small" effect="light">已验证</el-tag>
        </div>
        <el-form v-else label-position="top" class="user-settings-form" @submit.prevent="submitBindEmail">
          <el-form-item label="邮箱">
            <el-input v-model="emailForm.email" placeholder="请输入真实邮箱" autocomplete="email" />
          </el-form-item>
          <el-form-item label="图形验证码">
            <div class="captcha-row">
              <el-input v-model="emailForm.captcha" placeholder="验证码" />
              <img
                v-if="captchaImage"
                :src="captchaImage"
                class="captcha-img"
                alt="验证码"
                @click="refreshCaptcha"
              />
            </div>
          </el-form-item>
          <el-form-item label="邮箱验证码">
            <div class="captcha-row">
              <el-input v-model="emailForm.code" placeholder="邮箱验证码" />
              <el-button :disabled="sendingCode || codeCooldown > 0" @click="sendEmailCode">
                {{ codeCooldown > 0 ? `${codeCooldown}s` : '发送验证码' }}
              </el-button>
            </div>
          </el-form-item>
          <el-button type="primary" :loading="emailSaving" native-type="submit">绑定邮箱</el-button>
        </el-form>
      </section>

      <!-- 手机 -->
      <section class="account-section">
        <h3 class="section-title">绑定手机</h3>
        <p class="section-hint">
          绑定并验证手机号后，可用短信验证码登录。验证码会发到该号码。
        </p>
        <div v-if="phoneVerified && phone" class="email-bound">
          已绑定：<span class="mono">{{ maskedPhone }}</span>
          <el-tag type="success" size="small" effect="light">已验证</el-tag>
        </div>
        <el-form v-else label-position="top" class="user-settings-form" @submit.prevent="submitBindPhone">
          <el-form-item label="手机号">
            <el-input v-model="phoneForm.phone" maxlength="11" placeholder="请输入11位手机号" autocomplete="tel" />
          </el-form-item>
          <el-form-item label="图形验证码">
            <div class="captcha-row">
              <el-input v-model="phoneForm.captcha" placeholder="验证码" />
              <img
                v-if="phoneCaptchaImage"
                :src="phoneCaptchaImage"
                class="captcha-img"
                alt="验证码"
                @click="refreshPhoneCaptcha"
              />
            </div>
          </el-form-item>
          <el-form-item label="短信验证码">
            <div class="captcha-row">
              <el-input v-model="phoneForm.code" placeholder="短信验证码" maxlength="6" />
              <el-button :disabled="sendingPhoneCode || phoneCodeCooldown > 0" @click="sendPhoneCode">
                {{ phoneCodeCooldown > 0 ? `${phoneCodeCooldown}s` : '发送验证码' }}
              </el-button>
            </div>
          </el-form-item>
          <el-button type="primary" :loading="phoneSaving" native-type="submit">绑定手机</el-button>
        </el-form>
      </section>

      <div class="footer-links">
        <router-link to="/user/info">去修改昵称与个人朋友信息 →</router-link>
      </div>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage } from 'element-plus'
import { apiErrorText } from '@/utils/http'
import { validatePassword, passwordsMatch, PASSWORD_HINT } from '@/utils/password'
import {
  WECHAT_USERNAME_HINT,
  validateWechatUsernameChange,
  isAutoGeneratedUsername
} from '@/utils/username'
import { syncAccountFlagsToStorage } from '@/utils/accountSetup'
import { resolvePostLoginRoute } from '@/utils/referralAuth'
import { startEmailCodeCooldown } from '@/utils/emailCodeCooldown'
import { createCaptchaRefresher } from '@/utils/captchaCooldown'

const emptyPwd = () => ({
  newPassword: '',
  confirmPassword: ''
})

export default {
  name: 'UserAccountSecurity',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const passwordHint = PASSWORD_HINT
    const wechatUsernameHint = WECHAT_USERNAME_HINT

    const currentUsername = ref('')
    const usernameAuto = ref(false)
    const usernameChangeAllowed = ref(false)
    const passwordUnset = ref(false)
    const email = ref('')
    const emailVerified = ref(false)
    const phone = ref('')
    const phoneVerified = ref(false)

    const newUsername = ref('')
    const usernameSaving = ref(false)
    const setup = reactive({ username: '', newPassword: '', confirmPassword: '' })
    const setupSaving = ref(false)

    const pwdFormRef = ref(null)
    const pwdForm = reactive(emptyPwd())
    const pwdSaving = ref(false)

    const emailForm = reactive({ email: '', code: '', captcha: '', captchaId: '' })
    const captchaImage = ref('')
    const sendingCode = ref(false)
    const codeCooldown = ref(0)
    const emailSaving = ref(false)
    const codeTimerHolder = { current: null }

    const phoneForm = reactive({ phone: '', code: '', captcha: '', captchaId: '' })
    const phoneCaptchaImage = ref('')
    const sendingPhoneCode = ref(false)
    const phoneCodeCooldown = ref(0)
    const phoneSaving = ref(false)
    const phoneTimerHolder = { current: null }

    const maskedPhone = computed(() => {
      const p = String(phone.value || '')
      if (p.length < 7) return p || '—'
      return `${p.slice(0, 3)}****${p.slice(-4)}`
    })

    const needsCombinedSetup = computed(
      () => passwordUnset.value && (usernameAuto.value || usernameChangeAllowed.value)
    )

    // 微信扫码登录被强制引导到本页时带 setup=1
    const setupRedirect = computed(() => route.query.setup === '1')

    // 完成登录名+密码后回跳原目标页
    const backToOriginalPage = async () => {
      const href = typeof route.query.href === 'string' ? route.query.href : ''
      if (
        !href.startsWith('/') ||
        href.startsWith('//') ||
        href === '/' ||
        href.startsWith('/user/password')
      ) {
        return
      }
      const target = await resolvePostLoginRoute(href)
      setTimeout(() => {
        router.replace(target)
      }, 800)
    }

    const authHeaders = () => ({
      Authorization: 'Bearer ' + (localStorage.getItem('jwt_token') || '')
    })

    const applyInfo = (data) => {
      if (!data) return
      currentUsername.value = data.username || ''
      const stillAuto = isAutoGeneratedUsername(currentUsername.value)
      // 以「当前是否仍为系统临时名」为准，兼容历史 username_customized 误标
      usernameAuto.value = stillAuto || !!data.usernameAuto
      usernameChangeAllowed.value =
        stillAuto ||
        (data.usernameChangeAllowed !== undefined
          ? !!data.usernameChangeAllowed
          : !!data.usernameAuto)
      if (usernameChangeAllowed.value && !stillAuto && !isAutoGeneratedUsername(currentUsername.value)) {
        usernameChangeAllowed.value = false
      }
      passwordUnset.value = !!data.passwordUnset
      email.value = data.email || ''
      emailVerified.value = Number(data.isEmailVerified) === 1
      phone.value = data.phone || ''
      phoneVerified.value = Number(data.isPhoneVerified) === 1
      syncAccountFlagsToStorage({
        username: currentUsername.value,
        usernameAuto: usernameAuto.value,
        usernameChangeAllowed: usernameChangeAllowed.value,
        usernameCooldownDaysLeft: 0,
        passwordUnset: passwordUnset.value
      })
    }

    const fetchInfo = async () => {
      try {
        const { data } = await axios.post('/api/v1/user/info', {}, { headers: authHeaders() })
        if (data.code === 200 && data.data) {
          applyInfo(data.data)
        }
      } catch (e) {
        console.warn('fetch account info failed', e)
      }
    }

    // 图形验证码：防连续点击，上一次完成后 2s 内不再重复获取
    const refreshCaptcha = createCaptchaRefresher(async () => {
      try {
        const { data } = await axios.get('/api/v1/user/captcha')
        if (data.code === 200 && data.data) {
          captchaImage.value = data.data.imageBase64 || data.data.captchaImage || data.data.image || ''
          emailForm.captchaId = data.data.captchaId || data.data.id || ''
          emailForm.captcha = ''
        }
      } catch (e) {
        ElMessage.error('获取验证码失败')
      }
    })

    const refreshPhoneCaptcha = createCaptchaRefresher(async () => {
      try {
        const { data } = await axios.get('/api/v1/user/captcha')
        if (data.code === 200 && data.data) {
          phoneCaptchaImage.value = data.data.imageBase64 || data.data.captchaImage || data.data.image || ''
          phoneForm.captchaId = data.data.captchaId || data.data.id || ''
          phoneForm.captcha = ''
        }
      } catch (e) {
        ElMessage.error('获取验证码失败')
      }
    })

    const validPhone = (raw) => /^1[3-9]\d{9}$/.test(String(raw || '').trim())

    const validateConfirm = (_rule, value, callback) => {
      if (!passwordsMatch(pwdForm.newPassword, value)) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    const validateNewPwd = (_rule, value, callback) => {
      const r = validatePassword(value)
      if (!r.ok) callback(new Error(r.message || '密码不符合要求'))
      else callback()
    }

    const pwdRules = computed(() => {
      return {
        newPassword: [{ required: true, validator: validateNewPwd, trigger: 'blur' }],
        confirmPassword: [{ required: true, validator: validateConfirm, trigger: 'blur' }]
      }
    })

    const submitCombinedSetup = async () => {
      const payload = {}
      if (usernameChangeAllowed.value) {
        const v = validateWechatUsernameChange(setup.username)
        if (!v.ok) {
          ElMessage.warning(v.message)
          return
        }
        payload.username = setup.username.trim()
      }
      if (passwordUnset.value) {
        const pv = validatePassword(setup.newPassword)
        if (!pv.ok) {
          ElMessage.warning(pv.message || '密码不符合要求')
          return
        }
        if (!passwordsMatch(setup.newPassword, setup.confirmPassword)) {
          ElMessage.warning('两次输入的密码不一致')
          return
        }
        payload.newPassword = setup.newPassword
        payload.confirmPassword = setup.confirmPassword
      }
      setupSaving.value = true
      try {
        const { data } = await axios.post('/api/v1/user/account/setup', payload, {
          headers: authHeaders()
        })
        if (data.code !== 200) {
          ElMessage.error(data.message || '保存失败')
          return
        }
        ElMessage.success(data.message || '已保存')
        setup.username = ''
        setup.newPassword = ''
        setup.confirmPassword = ''
        await fetchInfo()
        // 登录名与密码均已完善：回跳登录前的原页面
        if (!usernameAuto.value && !passwordUnset.value) {
          await backToOriginalPage()
        }
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '保存失败')
      } finally {
        setupSaving.value = false
      }
    }

    const submitUsernameOnly = async () => {
      const v = validateWechatUsernameChange(newUsername.value)
      if (!v.ok) {
        ElMessage.warning(v.message)
        return
      }
      usernameSaving.value = true
      try {
        const { data } = await axios.post(
          '/api/v1/user/username',
          { username: newUsername.value.trim() },
          { headers: authHeaders() }
        )
        if (data.code !== 200) {
          ElMessage.error(data.message || '修改失败')
          return
        }
        ElMessage.success(data.message || '登录名已更新')
        newUsername.value = ''
        await fetchInfo()
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '修改失败')
      } finally {
        usernameSaving.value = false
      }
    }

    const submitPassword = async () => {
      try {
        await pwdFormRef.value?.validate()
      } catch {
        return
      }
      pwdSaving.value = true
      try {
        const url = passwordUnset.value
          ? '/api/v1/user/password/set-initial'
          : '/api/v1/user/password'
        const payload = {
          newPassword: pwdForm.newPassword,
          confirmPassword: pwdForm.confirmPassword
        }
        const { data } = await axios.post(url, payload, { headers: authHeaders() })
        if (data.code !== 200) {
          ElMessage.error(data.message || '保存失败')
          return
        }
        ElMessage.success(data.message || '密码已保存')
        Object.assign(pwdForm, emptyPwd())
        passwordUnset.value = false
        await fetchInfo()
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '保存失败')
      } finally {
        pwdSaving.value = false
      }
    }

    const sendEmailCode = async () => {
      if (!emailForm.email.trim()) {
        ElMessage.warning('请先填写邮箱')
        return
      }
      if (!emailForm.captcha || !emailForm.captchaId) {
        ElMessage.warning('请填写图形验证码')
        return
      }
      sendingCode.value = true
      try {
        const { data } = await axios.post('/api/v1/user/sendemailcode', {
          email: emailForm.email.trim(),
          type: 'bind',
          captcha: emailForm.captcha,
          captchaId: emailForm.captchaId
        })
        if (data.code !== 200) {
          ElMessage.error(data.message || '发送失败')
          await refreshCaptcha()
          return
        }
        ElMessage.success('验证码已发送')
        startEmailCodeCooldown(codeCooldown, codeTimerHolder, () => {
          refreshCaptcha()
        })
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '发送失败')
        await refreshCaptcha()
      } finally {
        sendingCode.value = false
      }
    }

    const submitBindEmail = async () => {
      if (!emailForm.email.trim() || !emailForm.code.trim()) {
        ElMessage.warning('请填写邮箱与验证码')
        return
      }
      emailSaving.value = true
      try {
        const { data } = await axios.post(
          '/api/v1/user/bind/email',
          { email: emailForm.email.trim(), code: emailForm.code.trim() },
          { headers: authHeaders() }
        )
        if (data.code !== 200) {
          ElMessage.error(data.message || '绑定失败')
          return
        }
        ElMessage.success(data.message || '绑定成功')
        emailForm.code = ''
        await fetchInfo()
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '绑定失败')
      } finally {
        emailSaving.value = false
      }
    }

    const sendPhoneCode = async () => {
      const num = phoneForm.phone.trim()
      if (!validPhone(num)) {
        ElMessage.warning('请填写11位手机号')
        return
      }
      if (!phoneForm.captcha || !phoneForm.captchaId) {
        ElMessage.warning('请填写图形验证码')
        return
      }
      sendingPhoneCode.value = true
      try {
        const { data } = await axios.post('/api/v1/user/sendphonecode', {
          phonenum: num,
          captcha: phoneForm.captcha,
          captchaId: phoneForm.captchaId
        })
        if (data.code !== 200) {
          ElMessage.error(apiErrorText(data, '发送失败'))
          await refreshPhoneCaptcha()
          return
        }
        ElMessage.success('验证码已发送')
        startEmailCodeCooldown(phoneCodeCooldown, phoneTimerHolder, () => {
          refreshPhoneCaptcha()
        })
      } catch (e) {
        ElMessage.error(apiErrorText(e.response?.data, '发送失败'))
        await refreshPhoneCaptcha()
      } finally {
        sendingPhoneCode.value = false
      }
    }

    const submitBindPhone = async () => {
      const num = phoneForm.phone.trim()
      if (!validPhone(num) || !phoneForm.code.trim()) {
        ElMessage.warning('请填写手机号与短信验证码')
        return
      }
      phoneSaving.value = true
      try {
        const { data } = await axios.post(
          '/api/v1/user/bind/phone',
          { phone: num, code: phoneForm.code.trim() },
          { headers: authHeaders() }
        )
        if (data.code !== 200) {
          ElMessage.error(data.message || '绑定失败')
          return
        }
        ElMessage.success(data.message || '绑定成功')
        phoneForm.code = ''
        await fetchInfo()
      } catch (e) {
        ElMessage.error(e.response?.data?.message || '绑定失败')
      } finally {
        phoneSaving.value = false
      }
    }

    onMounted(() => {
      fetchInfo()
      refreshCaptcha()
      refreshPhoneCaptcha()
    })
    onBeforeUnmount(() => {
      if (codeTimerHolder.current) clearInterval(codeTimerHolder.current)
      if (phoneTimerHolder.current) clearInterval(phoneTimerHolder.current)
    })

    return {
      passwordHint,
      wechatUsernameHint,
      currentUsername,
      usernameAuto,
      usernameChangeAllowed,
      passwordUnset,
      email,
      emailVerified,
      phone,
      phoneVerified,
      maskedPhone,
      needsCombinedSetup,
      setupRedirect,
      newUsername,
      usernameSaving,
      setup,
      setupSaving,
      pwdFormRef,
      pwdForm,
      pwdRules,
      pwdSaving,
      emailForm,
      captchaImage,
      sendingCode,
      codeCooldown,
      emailSaving,
      submitCombinedSetup,
      submitUsernameOnly,
      submitPassword,
      sendEmailCode,
      submitBindEmail,
      refreshCaptcha,
      phoneForm,
      phoneCaptchaImage,
      sendingPhoneCode,
      phoneCodeCooldown,
      phoneSaving,
      refreshPhoneCaptcha,
      sendPhoneCode,
      submitBindPhone
    }
  }
}
</script>

<style scoped>
.user-password-container {
  max-width: 720px;
  margin: 0 auto;
  padding: 16px;
}
.user-password-card { width: 100%; }
.card-title { margin: 0; font-size: 1.25rem; }
.page-hint {
  color: #606266;
  line-height: 1.6;
  margin: 0 0 20px;
  font-size: 14px;
}
.setup-alert {
  margin-bottom: 20px;
}
.account-section {
  border-top: 1px solid #ebeef5;
  padding: 20px 0;
}
.account-section--primary {
  border-top: none;
  background: #f5f9ff;
  margin: 0 -16px 8px;
  padding: 16px;
  border-radius: 8px;
}
.section-title {
  margin: 0 0 8px;
  font-size: 1.05rem;
}
.section-hint, .field-hint, .cooldown-hint {
  color: #909399;
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 12px;
}
.field-rule {
  color: #606266;
  font-size: 13px;
  line-height: 1.55;
  margin: 6px 0 0;
  padding: 8px 10px;
  background: #f4f4f5;
  border-radius: 6px;
}
.field-rule + .el-input {
  margin-top: 8px;
}
.cooldown-hint { color: #e6a23c; }
.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.inline-tags, .username-display, .email-bound {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}
.captcha-row {
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
}
.captcha-img {
  height: 36px;
  cursor: pointer;
  border-radius: 4px;
}
.footer-links {
  margin-top: 8px;
  padding-top: 12px;
  border-top: 1px solid #ebeef5;
}
.footer-links a { color: #409eff; text-decoration: none; }
</style>
